import { useState, useEffect } from 'react';
import { api } from '../services/api';
import type { ProblemContext } from '@attack100/shared';

interface DevToolsPanelProps {
  problemId: number;
}

type DevTab = 'context' | 'prompts';

export function DevToolsPanel({ problemId }: DevToolsPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<DevTab>('context');
  const [context, setContext] = useState<ProblemContext | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && !context) {
      loadContext();
    }
  }, [isOpen]);

  const loadContext = async () => {
    try {
      setLoading(true);
      const data = await api.getContext(problemId);
      setContext(data.context);
    } catch (error) {
      console.error('Failed to load context:', error);
    } finally {
      setLoading(false);
    }
  };

  const personas = [
    { id: 'architect', name: 'The Architect', description: 'Technical Design & Systems' },
    { id: 'economist', name: 'The Economist', description: 'Business Model & Revenue' },
    { id: 'psychologist', name: 'The Psychologist', description: 'User Needs & Experience' },
    { id: 'storyteller', name: 'The Storyteller', description: 'Brand & Communication' },
    { id: 'operator', name: 'The Operator', description: 'Processes & Execution' },
    { id: 'strategist', name: 'The Strategist', description: 'Competition & Positioning' },
    { id: 'builder', name: 'The Builder', description: 'Implementation & Prototyping' },
    { id: 'connector', name: 'The Connector', description: 'Partnerships & Networks' },
    { id: 'scientist', name: 'The Scientist', description: 'Data & Experimentation' },
    { id: 'red-team', name: 'The Red Team', description: 'Risks & Solutions' }
  ];

  if (!isOpen) {
    return (
      <div style={{ position: 'fixed', bottom: '1rem', right: '1rem', zIndex: 1000 }}>
        <button
          onClick={() => setIsOpen(true)}
          className="btn btn-secondary"
          style={{ fontSize: '0.75rem', padding: '0.5rem 1rem' }}
        >
          🛠️ Dev Tools
        </button>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '1rem',
      right: '1rem',
      width: '400px',
      maxHeight: '600px',
      background: 'white',
      border: '2px solid var(--primary)',
      borderRadius: '0.5rem',
      boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        borderBottom: '1px solid var(--gray-200)',
        background: 'var(--gray-50)'
      }}>
        <div style={{
          padding: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <strong style={{ fontSize: '0.875rem' }}>🛠️ Dev Tools</strong>
          <button
            onClick={() => setIsOpen(false)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: 'var(--gray-400)'
            }}
          >
            ×
          </button>
        </div>
        <div style={{ display: 'flex', borderTop: '1px solid var(--gray-200)' }}>
          <button
            onClick={() => setActiveTab('context')}
            style={{
              flex: 1,
              padding: '0.5rem',
              background: activeTab === 'context' ? 'white' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'context' ? '2px solid var(--primary)' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontWeight: activeTab === 'context' ? '600' : '400',
              color: activeTab === 'context' ? 'var(--primary)' : 'var(--gray-600)'
            }}
          >
            Context
          </button>
          <button
            onClick={() => setActiveTab('prompts')}
            style={{
              flex: 1,
              padding: '0.5rem',
              background: activeTab === 'prompts' ? 'white' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'prompts' ? '2px solid var(--primary)' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontWeight: activeTab === 'prompts' ? '600' : '400',
              color: activeTab === 'prompts' ? 'var(--primary)' : 'var(--gray-600)'
            }}
          >
            Prompts
          </button>
        </div>
      </div>

      <div style={{
        padding: '1rem',
        overflow: 'auto',
        flex: 1,
        fontSize: '0.8125rem'
      }}>
        {activeTab === 'prompts' ? (
          <div style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            <div style={{ marginBottom: '1rem', color: 'var(--gray-600)' }}>
              These are the AI prompts used to generate angles for each persona. The {'{title}'} and {'{description}'} placeholders are replaced with your problem details.
            </div>
            {personas.map((persona) => (
              <details key={persona.id} style={{ marginBottom: '1rem' }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: '600',
                  color: 'var(--primary)',
                  marginBottom: '0.5rem',
                  fontSize: '0.8125rem'
                }}>
                  {persona.name} - {persona.description}
                </summary>
                <div style={{
                  marginLeft: '1rem',
                  padding: '0.75rem',
                  background: 'var(--gray-50)',
                  borderRadius: '0.25rem',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.5',
                  color: 'var(--gray-700)'
                }}>
                  You are {persona.name} - an expert in {persona.description.toLowerCase()}.{'\n\n'}
                  Problem: {'{title}'}{'\n'}
                  Description: {'{description}'}{'\n\n'}
                  Your role is to identify ONE specific angle for attacking this problem.{'\n\n'}
                  Respond with ONLY a JSON object in this exact format:{'\n'}
                  {'{'}{'\n'}
                  {'  '}"title": "A concise, specific angle title (5-8 words)",{'\n'}
                  {'  '}"description": "A clear 1-2 sentence description of this angle and why it matters"{'\n'}
                  {'}'}{'\n\n'}
                  Be specific and actionable. Focus on ONE concrete aspect.
                </div>
              </details>
            ))}
          </div>
        ) : loading ? (
          <div style={{ color: 'var(--gray-500)' }}>Loading context...</div>
        ) : !context ? (
          <div style={{ color: 'var(--gray-500)' }}>
            No context data yet. Complete the interview to see it here.
          </div>
        ) : (
          <div style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ color: 'var(--primary)', fontWeight: '600', marginBottom: '0.25rem' }}>
                Universal Context
              </div>
              <div style={{ paddingLeft: '0.5rem' }}>
                <div><strong>Stage:</strong> {context.stage || <em>not set</em>}</div>
                <div><strong>Timeline:</strong> {context.timeline || <em>not set</em>}</div>
                <div><strong>Resources:</strong> {context.resources || <em>not set</em>}</div>
                <div><strong>Success:</strong> {context.success_criteria || <em>not set</em>}</div>
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ color: 'var(--primary)', fontWeight: '600', marginBottom: '0.25rem' }}>
                Economist Context
              </div>
              <div style={{ paddingLeft: '0.5rem' }}>
                <div><strong>Has Money:</strong> {context.has_money ? '✓ Yes' : '✗ No'}</div>
                {context.has_money && (
                  <div><strong>Flow:</strong> {context.money_flow || <em>not set</em>}</div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ color: 'var(--primary)', fontWeight: '600', marginBottom: '0.25rem' }}>
                Psychologist Context
              </div>
              <div style={{ paddingLeft: '0.5rem' }}>
                <div><strong>Has Users:</strong> {context.has_users ? '✓ Yes' : '✗ No'}</div>
                {context.has_users && (
                  <>
                    <div><strong>Count:</strong> {context.user_count || <em>not set</em>}</div>
                    <div><strong>Needs:</strong> {context.user_needs || <em>not set</em>}</div>
                  </>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ color: 'var(--primary)', fontWeight: '600', marginBottom: '0.25rem' }}>
                Architect Context
              </div>
              <div style={{ paddingLeft: '0.5rem' }}>
                <div><strong>Is Technical:</strong> {context.is_technical ? '✓ Yes' : '✗ No'}</div>
                {context.is_technical && (
                  <div><strong>Comfort:</strong> {context.tech_comfort || <em>not set</em>}</div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ color: 'var(--primary)', fontWeight: '600', marginBottom: '0.25rem' }}>
                Red Team Context
              </div>
              <div style={{ paddingLeft: '0.5rem' }}>
                <div><strong>Biggest Fear:</strong> {context.biggest_fear || <em>not set</em>}</div>
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ color: 'var(--primary)', fontWeight: '600', marginBottom: '0.25rem' }}>
                Strategist Context
              </div>
              <div style={{ paddingLeft: '0.5rem' }}>
                <div><strong>Competition:</strong> {context.competition_level || <em>not set</em>}</div>
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ color: 'var(--primary)', fontWeight: '600', marginBottom: '0.25rem' }}>
                Builder Context
              </div>
              <div style={{ paddingLeft: '0.5rem' }}>
                <div><strong>Shipping:</strong> {context.shipping_preference || <em>not set</em>}</div>
              </div>
            </div>

            <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--gray-200)', color: 'var(--gray-500)' }}>
              <div><strong>Created:</strong> {new Date(context.created_at).toLocaleString()}</div>
              <div><strong>Updated:</strong> {new Date(context.updated_at).toLocaleString()}</div>
            </div>
          </div>
        )}
      </div>

      {context && activeTab === 'context' && (
        <div style={{
          padding: '0.75rem 1rem',
          borderTop: '1px solid var(--gray-200)',
          background: 'var(--gray-50)'
        }}>
          <button
            onClick={loadContext}
            className="btn btn-secondary"
            style={{ width: '100%', fontSize: '0.8125rem', padding: '0.5rem' }}
          >
            🔄 Refresh
          </button>
        </div>
      )}
    </div>
  );
}
